import React, { useState, useEffect } from "react";
import { X, AlertTriangle } from "lucide-react";
import type { City } from "../../types/city";

interface CityModalProps {
  open: boolean;
  onClose: () => void;
  mode: "create" | "edit" | "delete";
  city?: City | null;
  onSave: (cityData: Record<string, unknown>) => Promise<void>;
}

export const CityModal: React.FC<CityModalProps> = ({
  open,
  onClose,
  mode,
  city,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    city_id: "",
    city_name: "",
    country_code: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Reset form when modal opens/closes or city changes
  useEffect(() => {
    if (open) {
      if (mode === "create") {
        setFormData({
          city_id: "",
          city_name: "",
          country_code: "",
        });
      } else if (city && (mode === "edit" || mode === "delete")) {
        setFormData({
          city_id: city.city_id,
          city_name: city.city_name,
          country_code: city.country_code,
        });
      }
      setError("");
    }
  }, [open, mode, city]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (mode === "create" || mode === "edit") {
      // Validate form
      if (!formData.city_id.trim()) {
        setError("City ID is required");
        return;
      }
      if (!/^[a-z0-9_]+$/.test(formData.city_id)) {
        setError("City ID must be in snake_case format (lowercase letters, numbers, and underscores only)");
        return;
      }
      if (!formData.city_name.trim()) {
        setError("City name is required");
        return;
      }
      if (!formData.country_code.trim()) {
        setError("Country code is required");
        return;
      }
      if (!/^[A-Z]{2}$/.test(formData.country_code)) {
        setError("Country code must be 2 uppercase letters (e.g., DK, US, UK)");
        return;
      }
    }

    setLoading(true);
    try {
      if (mode === "delete") {
        await onSave({});
      } else if (mode === "edit") {
        await onSave({
          city_name: formData.city_name.trim(),
          country_code: formData.country_code.trim().toUpperCase(),
        });
      } else {
        await onSave({
          city_id: formData.city_id.trim(),
          city_name: formData.city_name.trim(),
          country_code: formData.country_code.trim().toUpperCase(),
        });
      }
      onClose();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "An error occurred";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const getModalTitle = () => {
    switch (mode) {
      case "create":
        return "Add New City";
      case "edit":
        return "Edit City";
      case "delete":
        return "Delete City";
      default:
        return "";
    }
  };

  const getSubmitButtonText = () => {
    if (loading) return "Processing...";
    switch (mode) {
      case "create":
        return "Add City";
      case "edit":
        return "Save Changes";
      case "delete":
        return "Delete City";
      default:
        return "Save";
    }
  };

  const getSubmitButtonClass = () => {
    const baseClass =
      "inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed";

    if (mode === "delete") {
      return `${baseClass} text-white bg-red-600 hover:bg-red-700 focus:ring-red-500`;
    }
    return `${baseClass} text-white bg-blue-600 hover:bg-blue-700 focus:ring-blue-500`;
  };

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500/75 transition-opacity"
          onClick={onClose}
          aria-hidden="true"
        />

        {/* Center modal on screen */}
        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>

        {/* Modal panel */}
        <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                {getModalTitle()}
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 focus:outline-none"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Error message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <div className="flex items-center">
                  <AlertTriangle className="h-4 w-4 text-red-600 mr-2" />
                  <span className="text-sm text-red-600">{error}</span>
                </div>
              </div>
            )}

            {/* Content */}
            {mode === "delete" ? (
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Are you sure you want to delete the city{" "}
                      <span className="font-semibold">{city?.city_name}</span> ({city?.city_id})?
                      This action cannot be undone. The city cannot be deleted if it is used in any campaigns.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* City ID */}
                <div>
                  <label
                    htmlFor="city_id"
                    className="block text-sm font-medium text-gray-700"
                  >
                    City ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="city_id"
                    value={formData.city_id}
                    onChange={(e) =>
                      setFormData({ ...formData, city_id: e.target.value.toLowerCase() })
                    }
                    disabled={mode === "edit"}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="e.g., copenhagen"
                  />
                  {mode === "edit" ? (
                    <p className="mt-1 text-xs text-gray-500">
                      City ID cannot be changed after creation
                    </p>
                  ) : (
                    <p className="mt-1 text-xs text-gray-500">
                      Snake case format: lowercase letters, numbers, and underscores only
                    </p>
                  )}
                </div>

                {/* City Name */}
                <div>
                  <label
                    htmlFor="city_name"
                    className="block text-sm font-medium text-gray-700"
                  >
                    City Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="city_name"
                    value={formData.city_name}
                    onChange={(e) =>
                      setFormData({ ...formData, city_name: e.target.value })
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Copenhagen"
                  />
                </div>

                {/* Country Code */}
                <div>
                  <label
                    htmlFor="country_code"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Country Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="country_code"
                    value={formData.country_code}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        country_code: e.target.value.toUpperCase(),
                      })
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 uppercase"
                    placeholder="e.g., DK"
                    maxLength={2}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    2-letter country code (e.g., DK, US, UK)
                  </p>
                </div>
              </form>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className={getSubmitButtonClass()}
            >
              {getSubmitButtonText()}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:mr-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
